import {
  DoorIcon,
  InfoIcon,
  LeftArrowIcon,
  Typography,
} from "@codat/orchard-ui";
import { ButtonImage } from "api/company";
import classNames from "classnames";
import { useConfig } from "contexts/ConfigContext";

import Button from "components/Button";
import ImageButton from "components/ImageButton";
import Logo from "components/Logo";
import { SecuredByCodat } from "components/SecuredByCodat";
import SecondaryCta from "components/SecondaryCta";

import styles from "./RightHandPane.module.css";

export type PrimaryCtaProps = {
  image?: ButtonImage;
  label?: string;
  isLoading?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  onClick: (
    event: React.MouseEvent<HTMLButtonElement & HTMLAnchorElement>
  ) => void;
  analyticsName: string;
  eventProperties?: object;
};

export interface RightHandPaneProps {
  title?: React.ReactNode;
  onBackClick?: () => void;
  onExitClick?: () => void;
  onInfoClick: () => void;
  primaryCtaProps?: PrimaryCtaProps;
  secondaryCtaProps?: React.ComponentProps<typeof SecondaryCta>;
  footer?: string;
  isComplete: boolean;
}

const PrimaryCtaButton = (props: React.PropsWithChildren<PrimaryCtaProps>) => {
  const { hidden, analyticsName, eventProperties, ...buttonProps } = props;
  return props?.image?.default?.image.src ? (
    <ImageButton
      {...buttonProps}
      disabled={props.isLoading}
      image={props.image}
      className={styles.nextBtn}
      analyticsName={analyticsName}
      eventProperties={eventProperties}
    />
  ) : (
    <Button
      eventProperties={eventProperties}
      {...buttonProps}
      className={classNames(styles.nextBtn, {
        [styles.hidden]: hidden,
      })}
      label={props.label ?? "Next"}
      type="button"
      data-testid="primary-next-btn"
      aria-hidden={hidden}
      analyticsName={analyticsName}
    />
  );
};

export const RightHandPane = ({
  title,
  onBackClick,
  onExitClick,
  onInfoClick,
  primaryCtaProps,
  secondaryCtaProps,
  footer,
  children,
  isComplete,
}: React.PropsWithChildren<RightHandPaneProps>) => {
  const {
    state: { data: config },
  } = useConfig();

  return (
    <div
      className={classNames(styles.container, { [styles.show]: isComplete })}
    >
      {isComplete && (
        <>
          <div className={styles.nav}>
            <Logo
              logo={config.logo}
              className={classNames(styles.logo, styles.pullLeft)}
              clientName={config.clientName}
              data-testid="r-pane-client-logo"
            />
            {onBackClick && (
              <Button
                className={classNames(
                  styles.navLink,
                  styles.navBack,
                  styles.pullLeft
                )}
                variant="tertiary"
                onClick={onBackClick}
                data-testid="r-pane-back"
                analyticsName="Back"
              >
                <LeftArrowIcon className={styles.navIcon} />
                <span className={styles.navLabel}>Back</span>
              </Button>
            )}
            {onExitClick && (
              <Button
                className={classNames(styles.navLink, styles.navExit)}
                onClick={onExitClick}
                variant="tertiary"
                data-testid="r-pane-exit"
                analyticsName="Exit"
              >
                <DoorIcon className={styles.navIcon} />
                <span className={styles.navLabel}>Exit</span>
              </Button>
            )}
            <Button
              className={classNames(styles.navLink, styles.navInfo)}
              onClick={onInfoClick}
              variant="tertiary"
              data-testid="r-pane-info"
              analyticsName="Info"
            >
              <InfoIcon className={styles.navIcon} />
              <span className={styles.navLabel}>Info</span>
            </Button>
          </div>
          {
            // Allows temporary override of margin for links page, remove once no longer necessary
          }
          {title && typeof title === "string" ? (
            <Typography data-testid="page-title" variant="h3" as="h2">
              {title}
            </Typography>
          ) : (
            title
          )}

          <div className={styles.body}>{children}</div>

          <div className={styles.footer}>
            {footer ? (
              <Typography variant="p" as="p" className={styles.footerText}>
                {footer}
              </Typography>
            ) : (
              <>
                {secondaryCtaProps && <SecondaryCta {...secondaryCtaProps} />}

                {primaryCtaProps && <PrimaryCtaButton {...primaryCtaProps} />}
              </>
            )}
            <div className={styles.securedByWrapper}>
              <SecuredByCodat />
            </div>
          </div>
        </>
      )}
    </div>
  );
};
